import React, { useEffect, useState, useRef } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Button as MuiButton,
  Grid,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Divider as MuiDivider,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField as MuiTextField,
  Typography,
  Box,
  Checkbox,
  Menu,
  MenuItem,
  CircularProgress,
  Chip,
} from "@mui/material";
import type { ChipProps } from "@mui/material";
import { spacing } from "@mui/system";
import PDFViewerModal from "../../../../pages/PDFViewer";
import {
  ArrowDropDown,
  Delete,
  Download,
  Search,
  Add,
} from "@mui/icons-material";
import { Formik } from "formik";
import * as Yup from "yup";
import useAuth from "../../../../hooks/useAuth";
import useAppDispatch from "../../../../hooks/useAppDispatch";
import useAppSelector from "../../../../hooks/useAppSelector";
import {
  createInvoice,
  deleteInvoice,
  deleteInvoicesBatch,
  fetchInvoices,
  generateInvoicesFromUnitList,
  openInvoiceForm,
  patchInvoice,
  setInvoice,
  updateInvoicesBatchPaid,
  updateInvoicesBatchReminder,
  updateInvoicesBatchSent,
} from "../../../../redux/slices/invoice";
import { format } from "date-fns";
import { baseUrl } from "../../../../config";
import { fetchAssociation } from "../../../../redux/slices/association";
import { fetchUsers } from "../../../../redux/slices/user";
import CustomSnackbar from "../../SnackBar";
import useSnackbar from "../../SnackbarHook";
import { InvoiceRowInput } from "./InvoiceRowInput";
import { CreateContactModal } from "./CreateContactModal";

const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);
const Button = styled(MuiButton)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);
const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

function InvoiceForm({
  onInvoiceCreated,
}: {
  onInvoiceCreated?: (result: any) => void;
}) {
  const dispatch = useAppDispatch();
  const { invoice } = useAppSelector((state) => state.invoice);
  const { users } = useAppSelector((state) => state.user);
  const { currentUser } = useAuth();

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  // State for new contact modal
  const [openContactModal, setOpenContactModal] = useState(false);
  const [pendingSetFieldValue, setPendingSetFieldValue] = useState<any>(null);

  // Fetch all users for the dropdown
  useEffect(() => {
    if (associationId) {
      dispatch(fetchUsers({ associationId }));
    }
  }, [dispatch, associationId]);

  // Calculation functions
  const calculateRowTotal = (quantity: string, unitPrice: string): number => {
    const qty = parseFloat(quantity) || 0;
    const price = parseFloat(unitPrice) || 0;
    return qty * price;
  };

  const calculateSubtotal = (rows: any[]): number => {
    return rows.reduce((sum, row) => {
      return sum + calculateRowTotal(row.quantity, row.unitPrice);
    }, 0);
  };

  const calculateTax = (subtotal: number): number => {
    return Math.round(subtotal * 0.255 * 100) / 100; // 25.5% VAT
  };

  const calculateTotal = (subtotal: number, tax: number): number => {
    return subtotal + tax;
  };

  // Validation schema
  const validationSchema = Yup.object().shape({
    userId: Yup.number()
      .typeError("Valitse vastaanottaja")
      .required("Vastaanottaja on pakollinen"),
    dueDate: Yup.date()
      .typeError("Anna kelvollinen päivämäärä")
      .required("Eräpäivä on pakollinen")
      .min(new Date(), "Eräpäivän tulee olla tulevaisuudessa"),
    rows: Yup.array()
      .of(
        Yup.object().shape({
          description: Yup.string().required("Kuvaus on pakollinen"),
          quantity: Yup.string()
            .required("Määrä on pakollinen")
            .test("is-positive", "Määrän tulee olla positiivinen", (value) => {
              const num = parseFloat(value || "0");
              return num > 0;
            }),
          unitPrice: Yup.string()
            .required("Yksikköhinta on pakollinen")
            .test(
              "is-positive",
              "Yksikköhinnan tulee olla positiivinen",
              (value) => {
                const num = parseFloat(value || "0");
                return num > 0;
              }
            ),
        })
      )
      .min(1, "Vähintään yksi laskurivi vaaditaan"),
  });

  const handleContactCreated = (userId: number) => {
    if (pendingSetFieldValue) {
      pendingSetFieldValue("userId", userId);
      setPendingSetFieldValue(null);
    }
  };

  const handleSubmit = async (
    values: any,
    { resetForm, setSubmitting }: any
  ) => {
    try {
      // Convert rows to proper format (string -> number)
      const formattedRows = values.rows.map((row: any) => {
        const quantity = parseFloat(row.quantity);
        const unitPrice = parseFloat(row.unitPrice);

        // Extra validation to ensure no NaN values
        if (isNaN(quantity) || isNaN(unitPrice)) {
          throw new Error("Kaikki määrä- ja hintakentät on täytettävä");
        }

        return {
          description: row.description,
          quantity,
          unitPrice,
        };
      });

      const payload = {
        userId: values.userId,
        dueDate: values.dueDate,
        rows: formattedRows,
      };

      const result = await dispatch(
        createInvoice({ payload, associationId })
      ).unwrap();

      // Call the callback with the result (contains batchId, streamUrl, etc.)
      if (onInvoiceCreated && result) {
        onInvoiceCreated(result);
      }

      resetForm();
      dispatch(openInvoiceForm(false));
    } catch (error: any) {
      console.error("Failed to create invoice:", error);
      alert(
        `Laskun luonti epäonnistui: ${error.message || "Tuntematon virhe"}`
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={{
        userId: (invoice as any)?.user?.id || "",
        dueDate: invoice?.dueDate || "",
        rows: (invoice as any)?.rows || [
          { description: "", quantity: "", unitPrice: "" },
        ],
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        setFieldValue,
      }) => {
        const subtotal = calculateSubtotal(values.rows);
        const tax = calculateTax(subtotal);
        const total = calculateTotal(subtotal, tax);

        const addRow = () => {
          setFieldValue("rows", [
            ...values.rows,
            { description: "", quantity: "", unitPrice: "" },
          ]);
        };

        const removeRow = (index: number) => {
          const newRows = values.rows.filter(
            (_: any, i: number) => i !== index
          );
          setFieldValue("rows", newRows);
        };

        const updateRow = (index: number, field: string, value: string) => {
          const newRows = [...values.rows];
          newRows[index] = { ...newRows[index], [field]: value };
          setFieldValue("rows", newRows);
        };

        return (
          <Card mb={6}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Luo uusi lasku
              </Typography>
              <form onSubmit={handleSubmit}>
                {/* Section 1: Invoice Header */}
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      label="Vastaanottaja"
                      name="userId"
                      value={values.userId}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "new-contact") {
                          setPendingSetFieldValue(() => setFieldValue);
                          setOpenContactModal(true);
                        } else {
                          handleChange(e);
                        }
                      }}
                      fullWidth
                      variant="outlined"
                      my={2}
                      error={touched.userId && Boolean(errors.userId)}
                      helperText={
                        touched.userId && errors.userId
                          ? String(errors.userId)
                          : undefined
                      }
                    >
                      <MenuItem value="">Valitse vastaanottaja</MenuItem>
                      {users?.map((user: any) => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.firstName} {user.lastName} ({user.email})
                        </MenuItem>
                      ))}
                      <Divider />
                      <MenuItem
                        value="new-contact"
                        sx={{ color: "primary.main", fontWeight: 500 }}
                      >
                        + Lisää uusi kontakti...
                      </MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Eräpäivä"
                      name="dueDate"
                      type="date"
                      value={values.dueDate}
                      onChange={handleChange}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      my={2}
                      error={touched.dueDate && Boolean(errors.dueDate)}
                      helperText={
                        touched.dueDate && errors.dueDate
                          ? String(errors.dueDate)
                          : undefined
                      }
                    />
                  </Grid>
                </Grid>

                <Divider my={4} />

                {/* Section 2: Dynamic Invoice Rows */}
                <Typography variant="subtitle1" gutterBottom>
                  Laskurivit
                </Typography>
                {values.rows.map((row: any, index: number) => (
                  <InvoiceRowInput
                    key={index}
                    row={row}
                    rowTotal={calculateRowTotal(row.quantity, row.unitPrice)}
                    onChange={(field, value) => updateRow(index, field, value)}
                    onRemove={() => removeRow(index)}
                    canRemove={values.rows.length > 1}
                  />
                ))}
                {typeof errors.rows === "string" && (
                  <Typography color="error" variant="caption">
                    {errors.rows}
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<Add />}
                  onClick={addRow}
                  sx={{ mt: 2 }}
                >
                  Lisää rivi
                </Button>

                <Divider my={4} />

                {/* Section 3: Totals Display */}
                <Grid container spacing={2} justifyContent="flex-end">
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Välisumma (€)"
                      value={subtotal.toFixed(2)}
                      fullWidth
                      variant="outlined"
                      disabled
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="ALV 25.5% (€)"
                      value={tax.toFixed(2)}
                      fullWidth
                      variant="outlined"
                      disabled
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Yhteensä (€)"
                      value={total.toFixed(2)}
                      fullWidth
                      variant="outlined"
                      disabled
                      InputProps={{ readOnly: true }}
                      sx={{
                        "& .MuiInputBase-input": {
                          fontWeight: "bold",
                        },
                      }}
                    />
                  </Grid>
                </Grid>

                <Divider my={4} />

                {/* Section 4: Form Actions */}
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button type="submit" variant="contained" color="primary">
                    Luo lasku
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => dispatch(openInvoiceForm(false))}
                  >
                    Peruuta
                  </Button>
                </Box>
              </form>

              {/* New Contact Modal */}
              <CreateContactModal
                open={openContactModal}
                onClose={() => setOpenContactModal(false)}
                onContactCreated={handleContactCreated}
                associationId={associationId}
              />
            </CardContent>
          </Card>
        );
      }}
    </Formik>
  );
}

function InvoiceList({
  handleCreateInvoicesFromUnits,
  handleCreateNewInvoice,
  handleShowInvoice,
  handleDelete,
  loading,
}: any) {
  const dispatch = useAppDispatch();
  const { association, status: associationStatus } = useAppSelector(
    (state) => state.association
  );
  const { invoices, status: invoiceStatus } = useAppSelector(
    (state) => state.invoice
  );
  const { currentUser } = useAuth();

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const isReady = associationStatus === "idle" && invoiceStatus === "idle";

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const allSelected =
    invoices?.length > 0 && selectedIds.length === invoices.length;

  const handleDownload = async (rowFile: any) => {
    const filename = rowFile.file.filename;
    try {
      const token = localStorage.getItem("accessToken");

      const response = await fetch(`${baseUrl}/files/download/${filename}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      // Create a blob from the response
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Create a link element, set the URL and click it
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename); // Set the file name for download
      document.body.appendChild(link);
      link.click();

      // Cleanup: remove the link and revoke the object URL
      link?.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading the file:", error);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev: any) =>
      prev.includes(id) ? prev.filter((i: any) => i !== id) : [...prev, id]
    );
  };

  const handleBulkSend = async (ids: number[]) => {
    try {
      await dispatch(updateInvoicesBatchSent({ ids, associationId })).unwrap();
      // No need to show UI here; ui slice already did on fulfilled/rejected.
    } catch {
      // optional: nothing; error snackbar already shown by matcher
    }
  };

  const handleBulkReminder = async (ids: number[]) => {
    try {
      await dispatch(
        updateInvoicesBatchReminder({
          ids,
          associationId,
        })
      ).unwrap();
    } catch {
      // optional
    }
  };

  const handleBulkMarkPaid = async (ids: number[]) => {
    try {
      await dispatch(
        updateInvoicesBatchPaid({
          ids,
          associationId,
        })
      ).unwrap();
    } catch {
      // optional
    }
  };

  const handleBulkDelete = (ids: number[]) => {
    if (window.confirm(`Poistetaanko ${ids.length} laskua?`)) {
      dispatch(deleteInvoicesBatch(ids, associationId));
      setSelectedIds([]);
    }
  };

  const toggleSelectAll = () => {
    if (!invoices) return;
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(invoices.map((i: any) => i.id));
    }
  };

  const [actionMenuAnchorEl, setActionMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const isActionMenuOpen = Boolean(actionMenuAnchorEl);

  const openActionMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setActionMenuAnchorEl(event.currentTarget);
  };

  const closeActionMenu = () => {
    setActionMenuAnchorEl(null);
  };

  type InvoiceStatus =
    | "queued"
    | "pdf_generating"
    | "draft"
    | "failed"
    | "sent"
    | "reminded"
    | "paid";

  const getInvoiceStatusInfo = (
    status: InvoiceStatus
  ): {
    label: string;
    color: ChipProps["color"];
    variant?: "filled" | "outlined";
  } => {
    switch (status) {
      case "queued":
        return { label: "Jonossa", color: "default", variant: "outlined" };
      case "pdf_generating":
        return { label: "Luodaan", color: "info", variant: "outlined" };
      case "draft":
        return { label: "Luotu", color: "success", variant: "filled" };
      case "failed":
        return { label: "Epäonnistui", color: "error", variant: "filled" };
      case "sent":
        return { label: "Lähetetty", color: "primary", variant: "filled" };
      case "reminded":
        return {
          label: "Muistutus lähetetty",
          color: "warning",
          variant: "filled",
        };
      case "paid":
        return { label: "Maksettu", color: "success", variant: "filled" };
      default:
        return { label: "Tuntematon", color: "default", variant: "outlined" };
    }
  };

  useEffect(() => {
    dispatch(fetchAssociation(associationId));
  }, [dispatch, associationId]);

  return (
    <Card mb={6}>
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          pb: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" gutterBottom>
            Laskutus
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => handleCreateInvoicesFromUnits()}
              disabled={
                loading ||
                association?.iban === null ||
                association?.bic === null
              }
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Luo maksuunpanoluettelosta"
              )}
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleCreateNewInvoice}
              disabled={association?.iban === null || association?.bic === null}
            >
              Lisää lasku
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: selectedIds.length > 0 ? "#e3f2fd" : "#f5f5f5",
            borderRadius: 1,
            px: 2,
            py: 1.5,
            mt: 1,
          }}
        >
          <Typography variant="body2" fontWeight={500}>
            {selectedIds.length > 0
              ? `${selectedIds.length} laskua valittuna`
              : "Valitse laskuja käyttääksesi massatoimintoja"}
          </Typography>

          <Box>
            <Button
              variant="contained"
              color="primary"
              onClick={openActionMenu}
              disabled={selectedIds.length === 0}
              endIcon={<ArrowDropDown />}
            >
              Toiminnot
            </Button>

            <Menu
              anchorEl={actionMenuAnchorEl}
              open={isActionMenuOpen}
              onClose={closeActionMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem
                onClick={() => {
                  handleBulkSend(selectedIds);
                  closeActionMenu();
                }}
              >
                Lähetä lasku
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleBulkReminder(selectedIds);
                  closeActionMenu();
                }}
              >
                Lähetä muistutuslasku
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleBulkMarkPaid(selectedIds);
                  closeActionMenu();
                }}
              >
                Merkitse maksetuksi
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleBulkDelete(selectedIds);
                  closeActionMenu();
                }}
              >
                Poista
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </CardContent>
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  indeterminate={
                    selectedIds.length > 0 &&
                    selectedIds.length < invoices.length
                  }
                />
              </TableCell>
              <TableCell>Laskun numero</TableCell>
              <TableCell>Nimi</TableCell>
              <TableCell>Saaja</TableCell>
              <TableCell>Luontipäivä</TableCell>
              <TableCell>Eräpäivä</TableCell>
              <TableCell>Summa (€)</TableCell>
              <TableCell>Tila</TableCell>
              <TableCell>Toiminnot</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!isReady ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="50vh"
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Ei laskuja
                </TableCell>
              </TableRow>
            ) : (
              invoices.map((row: any) => (
                <TableRow key={row.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.includes(row.id)}
                      onChange={() => toggleSelect(row.id)}
                    />
                  </TableCell>
                  <TableCell>{row.invoiceNumber}</TableCell>
                  <TableCell>
                    {row.file?.originalname ?? "PDF puuttuu"}
                  </TableCell>
                  <TableCell>
                    {row.user.firstName + " " + row.user.lastName ||
                      "Ei tiedossa"}
                  </TableCell>
                  <TableCell>
                    {format(new Date(row.issueDate), "dd-MM-yyyy")}
                  </TableCell>
                  <TableCell>
                    {format(new Date(row.dueDate), "dd-MM-yyyy")}
                  </TableCell>
                  <TableCell>{row.total.toFixed(2)}</TableCell>
                  <TableCell>
                    {(() => {
                      const { label, color, variant } = getInvoiceStatusInfo(
                        row.status as InvoiceStatus
                      );
                      return (
                        <Chip
                          size="small"
                          label={label}
                          color={color}
                          variant={variant}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Download
                      color={!row.file?.filename ? "disabled" : "inherit"}
                      style={{
                        cursor: !row.file?.filename ? "not-allowed" : "pointer",
                      }}
                      onClick={
                        !row.file?.filename
                          ? undefined
                          : () => handleDownload(row)
                      }
                    />
                    <Search
                      color={!row.file?.filename ? "disabled" : "inherit"}
                      style={{
                        cursor: !row.file?.filename ? "not-allowed" : "pointer",
                      }}
                      onClick={
                        !row.file?.filename
                          ? undefined
                          : () => handleShowInvoice(row, true)
                      }
                    />
                    <Delete
                      color={!row.file?.filename ? "disabled" : "inherit"}
                      style={{
                        cursor: !row.file?.filename ? "not-allowed" : "pointer",
                      }}
                      onClick={() => handleBulkDelete([row.id])}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Card>
  );
}

function Invoice() {
  const dispatch = useAppDispatch();
  const { currentUser } = useAuth();
  const { open } = useAppSelector((state) => state.invoice);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfOpen, setPdfOpen] = useState(false);

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const [loading, setLoading] = useState(false);
  const esRef = useRef<EventSource | null>(null);
  const finishedIdsRef = useRef<Set<number>>(new Set());
  const expectedCountRef = useRef<number>(0);

  // Local snackbar state for progress countdown
  const {
    openSnackbar: localSnackbarOpen,
    snackbarMessage: localSnackbarMessage,
    snackbarSeverity: localSnackbarSeverity,
    progress: localProgress,
    setOpenSnackbar: setLocalSnackbarOpen,
    setSnackbarMessage: setLocalSnackbarMessage,
    setSnackbarSeverity: setLocalSnackbarSeverity,
    setProgress: setLocalProgress,
    duration: localDuration,
  } = useSnackbar();

  const handleCreateNewInvoice = () => {
    dispatch(setInvoice(undefined));
    dispatch(openInvoiceForm(true));
  };

  const handleShowInvoice = (invoice: any, isOpen: boolean) => {
    handleShowInvoiceModal(invoice, isOpen);
  };

  const handleDelete = (invoice: any) => {
    dispatch(deleteInvoice(invoice.id, associationId));
  };

  const handleShowInvoiceModal = (invoice: any, isOpen: boolean) => {
    const filename = invoice.file?.filename;
    if (!filename) return;

    setPdfUrl(`${baseUrl}/files/preview/${filename}`);
    setPdfOpen(isOpen);
  };

  const resetBatchCounters = (recipients: number) => {
    finishedIdsRef.current = new Set();
    expectedCountRef.current = recipients;
  };

  const maybeFinalize = async (es: EventSource) => {
    if (finishedIdsRef.current.size >= expectedCountRef.current) {
      await dispatch(fetchInvoices(associationId));
      es.close();
      if (esRef.current === es) esRef.current = null;
      setLoading(false);

      // Show success message with local snackbar
      setLocalSnackbarMessage(
        `${expectedCountRef.current} laskun PDF luotu onnistuneesti`
      );
      setLocalSnackbarSeverity("success");
      setLocalProgress(100);
      setLocalSnackbarOpen(true);
    }
  };

  const openInvoiceStream = async (
    batchId: string,
    streamUrl: string,
    recipients: number
  ) => {
    resetBatchCounters(recipients);

    // Load snapshot first
    await dispatch(fetchInvoices(associationId));

    // Open SSE with auth token
    if (esRef.current) esRef.current.close();

    // Get auth token from localStorage
    const token = localStorage.getItem("accessToken");
    if (!token) {
      console.error("No auth token found");
      setLoading(false);
      return;
    }

    const baseStreamUrl = streamUrl.startsWith("http")
      ? streamUrl
      : `${baseUrl}${streamUrl}`;
    const url = `${baseStreamUrl}?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    esRef.current = es;

    es.addEventListener("open", () => {});

    // Live patch for progress
    es.addEventListener("progress", (e: MessageEvent) => {
      const msg = JSON.parse(e.data);
      if (!msg?.invoiceId) return;

      // Patch UI
      dispatch(
        patchInvoice({
          invoiceId: msg.invoiceId,
          updates: {
            status: msg.status,
            pct: msg.pct,
            ...(msg.fileId ? { file: { id: msg.fileId } } : {}),
          },
        })
      );
    });

    // Count completion
    es.addEventListener("completed", (e: MessageEvent) => {
      const msg = JSON.parse(e.data);
      if (!msg?.invoiceId) return;

      // Patch to generated
      dispatch(
        patchInvoice({
          invoiceId: msg.invoiceId,
          updates: {
            status: msg.status ?? "draft",
            pct: 100,
            ...(msg.fileId ? { file: { id: msg.fileId } } : {}),
          },
        })
      );

      if (!finishedIdsRef.current.has(msg.invoiceId)) {
        finishedIdsRef.current.add(msg.invoiceId);
        maybeFinalize(es);
      }
    });

    es.addEventListener("failed", (e: MessageEvent) => {
      const msg = JSON.parse(e.data);
      if (!msg?.invoiceId) return;

      // Patch to failed
      dispatch(
        patchInvoice({
          invoiceId: msg.invoiceId,
          updates: {
            status: "failed",
            lastError: msg.reason ?? msg.lastError ?? "Tuntematon virhe",
          },
        })
      );

      if (!finishedIdsRef.current.has(msg.invoiceId)) {
        finishedIdsRef.current.add(msg.invoiceId);
        maybeFinalize(es);
      }
    });

    es.addEventListener("ping", () => {});
    es.addEventListener("error", () => {
      console.error("SSE error");
    });
  };

  const handleCreateInvoicesFromUnits = async () => {
    setLoading(true);
    try {
      const result = await dispatch(
        generateInvoicesFromUnitList(associationId)
      );

      const { batchId, recipients, streamUrl } = result;

      await openInvoiceStream(batchId, streamUrl, recipients);
    } catch (e: any) {
      console.error(e);
      setLoading(false);
      alert("Laskujen luonti epäonnistui");
    }
  };

  const handleManualInvoiceCreated = async (result: any) => {
    // Manual invoice was created, connect to SSE stream for PDF generation progress
    if (result?.batchId && result?.streamUrl && result?.recipients) {
      setLoading(true);

      // Inform user that PDF is being generated with local snackbar
      setLocalSnackbarMessage("Lasku luotu onnistuneesti. Luodaan PDF...");
      setLocalSnackbarSeverity("info");
      setLocalProgress(100);
      setLocalSnackbarOpen(true);

      try {
        await openInvoiceStream(
          result.batchId,
          result.streamUrl,
          result.recipients
        );
      } catch (e: any) {
        console.error("Failed to connect to invoice stream:", e);
        setLoading(false);
      }
    }
  };

  const handleCloseLocalSnackbar = () => {
    setLocalSnackbarOpen(false);
  };

  useEffect(() => {
    dispatch(fetchInvoices(associationId));
    return () => {
      dispatch(openInvoiceForm(false));
      if (esRef.current) esRef.current.close();
    };
  }, [dispatch, associationId]);

  return (
    <>
      <Helmet title="Laskut" />
      <Typography variant="h3" gutterBottom>
        Laskutus
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Link component={NavLink} to="/">
          Talous
        </Link>
        <Typography>Laskutus</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <PDFViewerModal
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        fileUrl={pdfUrl ?? ""}
        authToken={localStorage.getItem("accessToken")}
      />

      {open ? (
        <InvoiceForm onInvoiceCreated={handleManualInvoiceCreated} />
      ) : (
        <InvoiceList
          handleCreateInvoicesFromUnits={handleCreateInvoicesFromUnits}
          handleCreateNewInvoice={handleCreateNewInvoice}
          handleShowInvoice={handleShowInvoice}
          handleDelete={handleDelete}
          loading={loading}
        />
      )}

      <CustomSnackbar
        open={localSnackbarOpen}
        message={localSnackbarMessage}
        severity={localSnackbarSeverity}
        progress={localProgress}
        duration={localDuration}
        onClose={handleCloseLocalSnackbar}
      />
    </>
  );
}

export default Invoice;
